import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Loader2, UserPlus, Eye } from "lucide-react";

const List = ({ title, rows, render, testid, tone }) => rows?.length ? (
  <div className={`rounded-lg border p-3 text-xs ${tone}`} data-testid={testid}>
    <div className="font-bold mb-1">{title}</div>
    <div className="max-h-60 overflow-auto space-y-0.5">{rows.map((r, i) => <div key={i}>{render(r)}</div>)}</div>
  </div>
) : null;

export const NextFitMissingActive = ({ unitId, hasConfig }) => {
  const [job, setJob] = useState(null);
  const timer = useRef(null);
  const running = job && ["pending", "running"].includes(job.status);
  const load = async () => {
    try { const { data } = await api.get(`/integrations/nextfit/units/${unitId}/missing-active`); setJob(data.job); } catch { /* sem job */ }
  };
  useEffect(() => { setJob(null); load(); }, [unitId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearInterval(timer.current);
    if (running) { timer.current = setInterval(load, 2000); }
    return () => { clearInterval(timer.current); };
  }, [running, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (apply) => {
    if (apply && !window.confirm(`Cadastrar ${job?.result?.faltantes ?? 0} aluno(s) ativo(s) que faltaram, com contratos e mensalidades?`)) return;
    try { const { data } = await api.post(`/integrations/nextfit/units/${unitId}/missing-active?apply=${apply}`); setJob(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Não foi possível iniciar"); }
  };
  const r = job?.status === "done" ? job.result : null;
  const canApply = r && !r.apply && r.faltantes > 0;

  return (
    <Card className="p-5 sm:p-6" data-testid="missing-active-card">
      <div className="flex items-center gap-2 mb-1"><UserPlus className="w-5 h-5 text-rose-600"/><h3 className="font-heading text-lg font-black text-slate-900">Ativos só com nome que faltaram</h3></div>
      <p className="text-xs text-slate-500 mb-4">Procura no Next Fit os alunos ativos ou suspensos sem CPF (às vezes sem e-mail e telefone) que ainda não estão no American Fit, inclusive os que têm o mesmo nome de outro aluno. Primeiro mostra a lista; nada é gravado até você clicar em Trazer.</p>
      {!running && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => start(false)} disabled={!hasConfig} data-testid="missing-active-preview-btn"><Eye className="w-4 h-4 mr-1"/>Procurar faltantes</Button>
          {canApply && <Button onClick={() => start(true)} className="bg-rose-600 hover:bg-rose-700" data-testid="missing-active-apply-btn"><UserPlus className="w-4 h-4 mr-1"/>Trazer {r.faltantes} aluno(s)</Button>}
        </div>
      )}
      {running && (
        <div className="space-y-2" data-testid="missing-active-progress">
          <div className="flex justify-between text-sm font-semibold"><span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-rose-600"/>{job.label}</span><span className="font-mono" data-testid="missing-active-pct">{job.pct}%</span></div>
          <Progress value={job.pct} className="h-3"/>
        </div>
      )}
      {job?.status === "failed" && <div className="text-sm text-rose-700 mt-3" data-testid="missing-active-error">{job.error?.detail}</div>}
      {r && (
        <div className="mt-4 space-y-3 text-sm" data-testid="missing-active-result">
          <div className="font-semibold" data-testid="missing-active-summary">
            {r.apply ? `Concluído: ${r.alunos_criados} aluno(s) cadastrados, ${r.mensalidades_gravadas} mensalidade(s) gravadas${r.erros ? `, ${r.erros} erro(s)` : ""}.`
              : r.faltantes ? `${r.faltantes} aluno(s) ativo(s) faltando.` : "Nenhum aluno ativo faltando."}
          </div>
          {!r.apply && <List title="Serão cadastrados" rows={r.lista} testid="missing-active-list" tone="border-emerald-200 bg-emerald-50"
            render={(x) => <><b>{x.aluno}</b> · {x.ficara}{x.plano ? ` · ${x.plano}` : ""}{x.vencimento ? ` · vence ${String(x.vencimento).slice(0, 10)}` : ""}</>}/>}
          <List title={`${r.conferir_total} para conferir (mesmo telefone ou e-mail de alguém já cadastrado — não serão duplicados)`} rows={r.conferir}
            testid="missing-active-conferir" tone="border-amber-200 bg-amber-50" render={(x) => <><b>{x.aluno}</b> — {x.motivo}</>}/>
        </div>
      )}
    </Card>
  );
};
